import { useEffect, useState } from "react";

import { getBoardsWithRootCategory } from "../api/boardApi";
import { getCategoryInfo } from "../api/categoryAPI";
import { Post, getPostsBySubcategory, getOcspstLinkedPosts } from "../api/postAPI";
import { OCSPST_CATEGORY } from "../component/category/ocspstCategory";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (path: string) => `${BACKEND}/api/photo/${path}`;

// organ 하나당 대표 썸네일을 뽑기 위해 훑어보는 게시글 수 / 실제로 보관하는 썸네일 수
const SCAN_SIZE = 12;
export const MAX_THUMBS = 6;

export interface GalleryOrgan {
  id: number;
  name: string;
  postCnt: number;
  slideCnt: number;
  subNames: string[];
  /** 대표 썸네일 URL (최대 MAX_THUMBS 장). 로딩 전/없으면 빈 배열 */
  thumbs: string[];
  thumbsLoading: boolean;
}

// organ 목록(이름·건수)은 세션 내에서 거의 불변이라 모듈 레벨에 캐시한다.
// 시안 3개를 오가며 비교할 때 매번 재조회하지 않도록 썸네일도 함께 캐시.
let organsCache: GalleryOrgan[] | null = null;
let organsPromise: Promise<GalleryOrgan[]> | null = null;
const thumbsCache: Record<number, string[]> = {};

const fetchOrgans = async (): Promise<GalleryOrgan[]> => {
  const res = await getBoardsWithRootCategory();
  const rootId = res?.data?.[0]?.rootCategories?.[0]?.id;
  if (!rootId) return [];

  // getCategoryInfo 는 root → main → sub 트리를 한 번에 주므로 건수/서브 목록까지 1회 호출로 끝난다.
  const tree = await getCategoryInfo(rootId);
  const mains = [...(tree.children ?? [])].sort((a, b) => a.listOrder - b.listOrder);

  return mains.map((m) => ({
    id: m.id,
    name: m.name,
    postCnt: m.postCnt ?? 0,
    slideCnt: m.slideCnt ?? 0,
    subNames: [...(m.children ?? [])]
      .sort((a, b) => a.listOrder - b.listOrder)
      .map((s) => s.name),
    thumbs: thumbsCache[m.id] ?? [],
    thumbsLoading: !thumbsCache[m.id],
  }));
};

const loadOrgans = (): Promise<GalleryOrgan[]> => {
  if (organsCache) return Promise.resolve(organsCache);
  if (!organsPromise) {
    organsPromise = fetchOrgans()
      .then((list) => {
        organsCache = list;
        return list;
      })
      .catch((e) => {
        organsPromise = null; // 실패 시 다음 진입에서 재시도 가능하도록 캐시하지 않음
        throw e;
      });
  }
  return organsPromise;
};

// organ 하위 최신 게시글을 훑어 썸네일이 있는 것만 골라낸다. (비밀글 제외)
const fetchOrganThumbs = async (organId: number): Promise<string[]> => {
  if (thumbsCache[organId]) return thumbsCache[organId];

  const posts: Post[] = await getPostsBySubcategory(
    1,
    SCAN_SIZE,
    organId,
    "modifiedAt",
    "DESC",
    null,
    null
  );

  const thumbs = pickThumbs(Array.isArray(posts) ? posts : []);
  thumbsCache[organId] = thumbs;
  return thumbs;
};

// 썸네일이 있는 게시글만 골라 대표 썸네일 URL 로 바꾼다. (비밀글 제외)
const pickThumbs = (posts: Post[]): string[] =>
  posts
    .filter((p) => !p.isSecret)
    .map((p) => p.postThumbnail || p.slides?.find((s: any) => s?.thumbnail)?.thumbnail)
    .filter((path): path is string => !!path)
    .slice(0, MAX_THUMBS)
    .map(photoUrl);

/**
 * 소아고형암 온라인 자문시스템(OCSPST) 가상 카드.
 * 실제 organ 이 아니라 OCSPST 링크가 걸린 게시글 전체로 건수·썸네일을 만든다. (organ 합계에는 넣지 않는다)
 * 연결된 게시글이 없거나 불러오지 못하면 null.
 */
export const useOcspstGalleryCard = () => {
  const [card, setCard] = useState<GalleryOrgan | null>(null);

  useEffect(() => {
    let mounted = true;
    getOcspstLinkedPosts(1, 9999, "modifiedAt", "DESC").then((posts) => {
      if (!mounted || posts.length === 0) return;
      setCard({
        id: -1,
        name: OCSPST_CATEGORY.name,
        postCnt: posts.length,
        slideCnt: posts.reduce((sum, p: any) => sum + (p.slideCnt ?? 0), 0),
        subNames: [],
        thumbs: pickThumbs(posts.slice(0, SCAN_SIZE)),
        thumbsLoading: false,
      });
    });
    return () => {
      mounted = false;
    };
  }, []);

  return card;
};

/**
 * /gallery 신규 디자인 시안들이 공유하는 데이터 훅.
 * organ 목록(이름·게시글수·슬라이드수·서브 카테고리명)을 먼저 넘기고,
 * 대표 썸네일은 organ 별로 도착하는 대로 채워 넣는다.
 */
export const useGalleryOrgans = () => {
  const [organs, setOrgans] = useState<GalleryOrgan[]>(organsCache ?? []);
  const [loading, setLoading] = useState<boolean>(!organsCache);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;

    loadOrgans()
      .then((list) => {
        if (!mounted) return;
        setOrgans(list);
        setLoading(false);

        list.forEach((organ) => {
          if (!organ.thumbsLoading) return;
          fetchOrganThumbs(organ.id)
            .then((thumbs) => {
              if (!mounted) return;
              const patch = { thumbs, thumbsLoading: false };
              // 캐시된 organ 객체도 함께 갱신해 다음 시안 진입 시 썸네일이 바로 보이게 한다.
              if (organsCache) {
                organsCache = organsCache.map((o) => (o.id === organ.id ? { ...o, ...patch } : o));
              }
              setOrgans((prev) => prev.map((o) => (o.id === organ.id ? { ...o, ...patch } : o)));
            })
            .catch((e) => {
              console.error(`organ ${organ.id} thumbnail load error:`, e);
              if (mounted) {
                setOrgans((prev) =>
                  prev.map((o) => (o.id === organ.id ? { ...o, thumbsLoading: false } : o))
                );
              }
            });
        });
      })
      .catch((e) => {
        console.error("gallery organ load error:", e);
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  return { organs, loading, error };
};

export default useGalleryOrgans;
